import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, openAuthedFile } from "@/api/client";
import {
  INVOICE_TYPE_LABELS,
  INVOICE_STATUS_LABELS,
  type InvoiceType,
  type InvoiceStatus,
} from "@ckgo2/shared/types";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { FileText, Download, Loader2 } from "lucide-react";

interface InvoiceRow {
  id: string;
  number: string;
  orderId?: string | null;
  type: InvoiceType;
  status: InvoiceStatus;
  issueDate: string;
  dueDate?: string | null;
  currency: string;
  total: number;
  orderNumber?: string | null;
  customerFirstName?: string | null;
  customerLastName?: string | null;
}

interface FacetedInvoices {
  data: InvoiceRow[];
  total: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

const STATUS_BADGE: Record<InvoiceStatus, string> = {
  issued: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-gray-100 text-gray-500",
};

export default function InvoicesPage() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["invoices", qs],
    queryFn: () => api.get<FacetedInvoices>(`/api/admin/invoices?${qs}`),
  });

  const rows = data?.data ?? [];
  const facets = data?.facets ?? {};

  const openPdf = async (id: string) => {
    setPdfBusy(id);
    try {
      await openAuthedFile(`/api/admin/invoices/${id}/pdf`);
    } finally {
      setPdfBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoices</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Issued invoices — proforma, final, credit notes
        </p>
      </div>

      <FacetedListLayout
        searchPlaceholder="Search by invoice number…"
        total={data?.total ?? 0}
        limit={data?.limit ?? 25}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title="Type"
              paramKey="type"
              buckets={facets.type ?? []}
              labelFor={(v) => INVOICE_TYPE_LABELS[v as InvoiceType] ?? v}
            />
            <FacetPanel
              title="Status"
              paramKey="status"
              buckets={facets.status ?? []}
              labelFor={(v) => INVOICE_STATUS_LABELS[v as InvoiceStatus] ?? v}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <FileText className="mb-2 h-10 w-10 text-gray-300" />
              <p>No invoices</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {["Number", "Order", "Customer", "Type", "Total", "Status", "Due", ""].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 font-mono text-sm text-gray-900 dark:text-white">
                      {r.number}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {r.orderId ? (
                        <Link
                          to={`/orders/${r.orderId}`}
                          className="text-primary-600 dark:text-primary-400 font-mono hover:underline"
                        >
                          {r.orderNumber ?? "—"}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {[r.customerFirstName, r.customerLastName].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {INVOICE_TYPE_LABELS[r.type]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {formatPrice(r.total, r.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status]}`}
                      >
                        {INVOICE_STATUS_LABELS[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {r.dueDate ? formatDate(r.dueDate) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openPdf(r.id)}
                        className="btn-ghost btn-sm"
                        disabled={pdfBusy === r.id}
                        title="Open PDF"
                      >
                        {pdfBusy === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
